<template>
  <div class="reschedule" id="reschedule-view" tabindex="-1">
    <h2 v-if="appointmentExpired" class="text-center">{{ $t('messages.cancel_details_error') }}</h2>
    <div v-else>
      <h2 id="rescheduleHeading">{{ $t('Meeting information') }}</h2>
      <error-container v-if="bookingErrors.length > 0" :type="'booking'"></error-container>
      <p class="reschedule--bold"
        >{{ unitName }}
        <span v-if="appointmentDetails?.unit?.wca" :aria-label="$t('wheelchair_accessible')" style="margin-left: 2px"
          >&#xf193;</span
        >
      </p>
      <p class="reschedule--normal">{{ $t('Date of your reserved meeting') }}:</p>
      <p class="reschedule--bold" style="position: relative">
        <span aria-hidden="true" aria-labelledby="hiddenTime">{{ currentDate }}</span>
        <span id="timeSlot">{{ customFormatterScreenReader(currentDate) }}</span>
        <span id="hiddenTime" class="d-none">{{ customFormatterScreenReader(currentDate) }}</span>
      </p>
      <div class="section-separator section-separator--reschedule"></div>
      <div class="small-form">
        <div class="input-container">
          <label for="datepicker" class="input-container__label">{{ $t('Date') }}:</label>
          <VDatePicker
            v-model="datePickerValue"
            :min-date="disabledDates.to"
            :max-date="disabledDates.from"
            :disabled-dates="disabledDates.dates"
            :locale="calendarLang($i18n.locale)"
            :first-day-of-week="2"
            :popover="null"
            @popover-will-show="handlePopoverWillShow"
            @popover-did-show="handlePopoverDidShow"
            is-required
            ref="datepicker"
          >
            <template #default="{ inputEvents, togglePopover }">
              <input
                id="datepicker"
                @click="togglePopover"
                @keyup.enter="togglePopover"
                @keyup.space="togglePopover"
                class="input-container__input datepicker-input"
                :disabled="getAvailableDatesLoading"
                v-on="inputEvents"
                readonly
                :placeholder="datePickerInputText"
              />
            </template>
          </VDatePicker>
          <p class="input-error" v-if="bookingErrors.includes('date')">{{ $t('messages.date_view') }}</p>
        </div>
      </div>
      <!-- Available Times -->
      <fieldset aria-labeledby="meetingDurationHeading2">
        <div class="times-container times-container--reschedule" v-if="times && times.length !== 0">
          <p class="subheading" id="meetingDurationHeading2">{{ $t('Select the time') }}</p>
          <div class="time-button__container">
            <button
              class="time-button"
              @click="selectTime(time)"
              @mouseenter="removeAriaHidden"
              @mouseleave="addAriaHidden"
              v-for="(time, index) in times"
              :key="index"
              :class="{ 'time-button--selected': time == selectedTime }"
              :aria-live="time == selectedTime ? 'polite' : 'off'"
            >
              <img src="../assets/check-icon.svg" alt="selected" aria-label="selected" v-if="time == selectedTime" />
              <span class="sr-only" :aria-label="formatTimeForScreenReader(time.value)"> </span>
              <span :aria-hidden="isHovered ? 'false' : 'true'">{{ time.value }}</span>
            </button>
          </div>
          <p class="input-error" v-if="bookingErrors.includes('time')">{{ $t('messages.time_view') }}</p>
        </div>
      </fieldset>
      <div class="button__wrapper button__wrapper--margin-top-small">
        <button
          class="button button--primary mr-2"
          :class="{ 'button--disabled': appointmentDataLoading }"
          @click="submit()"
          >{{ $t('messages.reschedule_button') }}</button
        >
        <button class="button button--secondary" @click="cancel()">{{ $t('messages.cancel_btn') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useBookingStore, useRescheduleStore } from '../store'
import ErrorContainer from '../components/ErrorContainer.vue'
import moment from 'moment'
export default {
  name: 'RescheduleView',
  components: {
    ErrorContainer,
  },
  data() {
    return {
      appointmentDate: '',
      popover: {
        visibility: 'focus',
      },
      isHovered: false,
    }
  },
  created() {
    this.updateCountry(this.appCountry)
    this.updateLanguage(this.appLang)
    this.deleteTimes()
    this.getAppointmentData()
  },
  mounted() {},
  beforeUnmount() {
    this.clearErrors()
  },
  computed: {
    ...mapState(useBookingStore, [
      'appLang',
      'appCountry',
      'groupValue',
      'getTimesLoading',
      'times',
      'selectedDate',
      'selectedTime',
      'appointmentDataLoading',
      'getAvailableDatesLoading',
      'disabledDates',
      'bookingErrors',
    ]),
    ...mapState(useRescheduleStore, [
      'appointmentId',
      'processId',
      'appointmentExpired',
      'unitName',
      'currentDate',
      'subUnitId',
      'appointmentTypeId',
      'appointmentDetails',
      'meetingLang',
      'documentType',
      'documentNumber',
      'customerType',
      'childrenCount',
    ]),
    datePickerValue: {
      get() {
        return this.selectedDate
      },
      set(value) {
        this.selectDate(value)
      },
    },
    currentBranch() {
      return this.appointmentDetails.unit
    },
    datePickerInputText() {
      if (this.datePickerValue) {
        return this.customFormatter(this.datePickerValue)
      } else {
        return this.getAvailableDatesLoading ? `${this.$i18n.t('Please wait')}...` : this.$i18n.t('Select the date')
      }
    },
  },
  watch: {
    appointmentDetails() {
      if (Object.keys(this.appointmentDetails).length > 0) {
        this.requestAvailableDates()
      }
    },
    datePickerValue() {
      this.selectTime('')
    },
  },
  methods: {
    ...mapActions(useRescheduleStore, ['getAppointmentDetails', 'updateCountry', 'updateLanguage']),
    ...mapActions(useBookingStore, [
      'getTimes',
      'rescheduleAppointment',
      'cancelAppointment',
      'getAvailableDates',
      'setSelectedDate',
      'setSelectedTime',
      'deleteTimes',
      'enableWeekends',
      'disableWeekends',
      'setErrors',
      'clearErrors',
    ]),
    getAppointmentData() {
      const data = {
        appointmentId: this.appointmentId,
        processId: this.processId,
        pageLang: this.appLang,
        pageCountry: this.appCountry,
      }

      this.getAppointmentDetails(data).then(() => {
        document.getElementById('reschedule-view').focus()
      })
    },
    selectDate(date) {
      this.appointmentDate = moment(date).format('YYYY-MM-DD')
      this.setSelectedDate(this.appointmentDate)
      this.dateSelected = true
      this.getAppointmentTimes()
    },
    getAppointmentTimes() {
      //this.timeSlot = ''
      if (this.appointmentDate != '' && this.appointmentId != 0) {
        var lang = this.appLang
        var country = this.appCountry
        var unitId = this.subUnitId
        var groupValue = this.customerType
        var appointmentId = this.appointmentTypeId
        var date = this.appointmentDate
        var meetingLang = this.meetingLang
        var childrenCount = this.childrenCount

        this.getTimes({ unitId, groupValue, appointmentId, date, lang, country, meetingLang, childrenCount })
      }
    },
    customFormatter(date) {
      return moment(date).format('DD.MM.YYYY')
    },
    calendarLang() {
      switch (this.appLang) {
        case 'lv_LV':
          return 'lv'
        case 'en_US':
          return 'en'
        case 'ru_RU':
          return 'ru'
        case 'et_EE':
          return 'et'
        case 'tt_RU':
          return 'ru'
        case 'lt_LT':
          return 'lt'
      }
    },
    requestAvailableDates() {
      const unitId = this.subUnitId
      const groupValue = this.customerType
      const appointmentId = this.appointmentTypeId
      const lang = this.appLang
      const country = this.appCountry
      const meetingLang = this.meetingLang
      const childrenCount = this.childrenCount

      const object = { unitId, groupValue, appointmentId, lang, country, meetingLang, childrenCount }
      return this.getAvailableDates(object)
    },
    selectTime(value) {
      this.setSelectedTime(value)
    },
    submit() {
      this.clearErrors() //clear all errors on submit
      if (this.selectedDate == '' || this.selectedDate == null) {
        this.setErrors('date')
      }
      if (this.selectedBranch == '') {
        this.setErrors('branch')
      }
      if (this.selectedTime == '') {
        this.setErrors('time')
      }
      if (this.bookingErrors.length == 0) {
        this.bookAppointment()
      }
    },
    bookAppointment() {
      if (this.selectedTime && this.selectedTime.time_slot && !this.appointmentDataLoading) {
        const data = this.appointmentDetails

        let appointmentDataObject = new FormData()

        appointmentDataObject.append('lang', this.appLang)
        appointmentDataObject.append('country', this.appCountry)
        appointmentDataObject.append('group', data.group)
        appointmentDataObject.append('sub_unit_id', this.subUnitId)
        appointmentDataObject.append('appointment_type_id', this.appointmentTypeId)
        appointmentDataObject.append('date', this.selectedDate)
        appointmentDataObject.append('time_slot', this.selectedTime.time_slot)
        appointmentDataObject.append('customer[name]', data.customer.name)
        appointmentDataObject.append('customer[email]', data.customer.email)
        appointmentDataObject.append('customer[personal_code]', data.customer.personal_code)
        appointmentDataObject.append('customer[phone]', data.customer.phone)
        appointmentDataObject.append('comments', data.customer.comments)
        appointmentDataObject.append('appointment_id', this.appointmentId)
        appointmentDataObject.append('process_id', this.processId)
        appointmentDataObject.append('meeting_lang', this.meetingLang)
        appointmentDataObject.append('document_type', this.documentType)
        appointmentDataObject.append('document_number', this.documentNumber)

        if (data.group == 'JP') {
          appointmentDataObject.append('customer[company_name]', data.customer.company_name)
          appointmentDataObject.append('customer[registration_number]', data.customer.registration_nr)
        }
        if (data.customer.children && data.customer.children.length > 0) {
          data.customer.children.forEach((el, index) => {
            appointmentDataObject.append(`customer[children][${index + 1}][name]`, el.name)
            appointmentDataObject.append(`customer[children][${index + 1}][personal_code]`, el.personal_code)
            appointmentDataObject.append(`customer[children][${index + 1}][document_number]`, el.document_number)
          })
          appointmentDataObject.append(`children_count`, data.customer.children.length)
        }
        if (data.customer.representative) {
          appointmentDataObject.append('customer[representative][name]', data.customer.representative.name)
          appointmentDataObject.append(
            'customer[representative][personal_code]',
            data.customer.representative.personal_code
          )
        }

        this.hasValidAppointmentData = true
        this.rescheduleAppointment(appointmentDataObject)
      }
    },
    cancel() {
      const data = {
        appointmentId: this.appointmentId,
        processId: this.processId,
      }
      this.cancelAppointment(data)
    },
    async handlePopoverWillShow() {
      const firstAvailableDate = this.datePickerValue ? new Date(this.datePickerValue) : new Date(this.disabledDates.to)
      this.$nextTick(() => {
        this.$refs.datepicker.calendarRef.move(firstAvailableDate)
      })
    },
    async handlePopoverDidShow() {
      // focus on the initial date cell
      const firstAvailableDate = this.datePickerValue ? new Date(this.datePickerValue) : new Date(this.disabledDates.to)
      this.$nextTick(() => {
        if (firstAvailableDate && !this.$refs.datepicker.calendarRef.focusedDay) {
          this.$refs.datepicker.calendarRef.tryFocusDate(firstAvailableDate)
        }
      })
    },
    customFormatterScreenReader(date) {
      if (date) {
        const screenReaderDate = moment(date, 'DD.MM.YYYY HH:mm').format('D MMMM, YYYY, h:mm A')
        return screenReaderDate
      }
    },
    formatTimeForScreenReader(time) {
      return moment(time, 'HH:mm').format('h:mm A')
    },
    removeAriaHidden() {
      this.isHovered = true
    },
    addAriaHidden() {
      this.isHovered = false
    },
  },
}
</script>

<style scoped>
#timeSlot {
  position: absolute;
  left: 0;
  opacity: 0;
  width: 130px;
  height: 20px;
}
</style>
